import { FC, useEffect, useMemo, useState } from 'react';
import { connect, type ConnectedProps, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { RootState } from '../../lib/store';
import { getETFs, getSectorDetail } from '../../lib/store/fundamental';
import {
  DEFAULT_SEARCH_STOCK_LIMIT,
  NO_MORE_PAGES_TO_READ,
} from '../../lib/store/fundamental/constants';
import { FundamentalCall, SectorEnum } from '../../lib/store/fundamental/types';
import { isCallStatusPending } from '../../lib/util/error-handling/StatusByCallHelpers';

import { usePriceAndChartLoading, useSubPageTitle } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import { PopularGroupItemRow } from '../../shared_components/PopularGroups/PopularGroups.types';
import SharedPopularGroupTable from '../../shared_components/PopularGroups/PopularGroupTable/PopularGroupTable';
import { UI_ROUTES } from '../../util/routes';

import { NewOnThePopularGroupsData } from './PopularGroups.types';

const PopularGroupTableWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    companyLogoAndName,
    ETFData,
    sectorDetails,
    isGettingETFDetails,
    isGettingSectorDetails,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const titleRef = useSubPageTitle<HTMLDivElement>();
  const navigate = useNavigate();
  const locationState = location.state as { group: NewOnThePopularGroupsData; sector: string };
  const { id, symbolCount } = locationState.group;
  const { sector } = locationState;

  const [showLoadingSpinner, setShowLoadingSpinner] = useState<boolean>(false);
  const isEtfGroup = sector === SectorEnum.ETFs;
  const items = isEtfGroup ? ETFData.content : sectorDetails.content;

  // Single market: the price cache is keyed by the plain symbol and prices are in dollars.
  const rows = useMemo((): PopularGroupItemRow[] => items.map(item => ({
    item,
    chartSymbol: item.symbol,
    countryDesignation: '',
    currencySymbol: CurrencySymbolEnum.USD,
    isEuropeanSymbol: false,
    companyName: item.companyName ?? companyLogoAndName[item.symbol]?.name ?? '',
  })), [items, companyLogoAndName]);

  useEffect(() => {
    if (sectorDetails.sectorId !== id) {
      if (sector === SectorEnum.ETFs) return;
      dispatch(getSectorDetail(Number(id)));
    }
  }, [dispatch, sector, id, sectorDetails.content, sectorDetails.sectorId]);

  useEffect(() => {
    if (isEtfGroup && ETFData?.content?.length === 0) {
      dispatch(getETFs({ size: DEFAULT_SEARCH_STOCK_LIMIT, page: ETFData.nextPage }));
    }
  }, [sector, dispatch]);

  const symbolsToSubscribe = useMemo(() => items.map(item => ({
    symbol: item.symbol,
    stockExchangeCode: item.stockExchangeCode,
    countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
  })), [items, companyLogoAndName]);

  const currentApiPending = isEtfGroup ? isGettingETFDetails : isGettingSectorDetails;

  const isLoading = usePriceAndChartLoading({
    stocks: items,
    isApiPending: currentApiPending && !showLoadingSpinner,
  });

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: symbolsToSubscribe.length > 0 && (
      (isEtfGroup && !isGettingETFDetails) || (!isEtfGroup && !isGettingSectorDetails)
    ),
  });

  const handleLoadMore = () => {
    if (isEtfGroup) {
      dispatch(getETFs({ size: DEFAULT_SEARCH_STOCK_LIMIT, page: ETFData.nextPage }));
    } else {
      dispatch(getSectorDetail(Number(id)));
    }
    setShowLoadingSpinner(true);
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  const hasMorePages = !(sectorDetails.nextPageToRead === NO_MORE_PAGES_TO_READ
    || ETFData.nextPage === NO_MORE_PAGES_TO_READ)
    && !isGettingETFDetails && !isGettingSectorDetails;

  return (
    <SharedPopularGroupTable
      rows={rows}
      sector={sector}
      symbolCount={symbolCount}
      isPending={isGettingETFDetails || isGettingSectorDetails}
      isLoading={isLoading}
      hasMorePages={hasMorePages}
      showLoadingSpinner={showLoadingSpinner}
      onLoadMore={handleLoadMore}
      onSymbolClick={handleSymbolClick}
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
  ETFData: state.fundamental.ETFData,
  sectorDetails: state.fundamental.sectorDetails,
  isGettingETFDetails: isCallStatusPending(FundamentalCall.getETFs, state.fundamental.statusByCall),
  isGettingSectorDetails: isCallStatusPending(FundamentalCall.getSectorDetails, state.fundamental.statusByCall),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PopularGroupTableWrapper);
